import { FilterOperators } from "../constants/FilterOperators";
import { ServiceResponse } from "../models/ServiceResponse";
import { TaskTypeService } from "../services/TaskTypeService";
import { UserService } from "../services/UserService";
import { PriorityService } from "../services/PriorityService";
import { StatusService } from "../services/StatusService";
import { ProcessInstanceStateService } from "../services/ProcessInstanceStateService";
import { CommonFiltersArguments } from "../utilities/CommonFiltersArguments";
import { ObjectField } from "../models/ObjectField";
import { LogitudeLookupService } from "../services/LogitudeLookupService";
import { WorkflowService } from "../services/WorkflowService";
import { DirectionService } from "./DirectionService";
import { TransportService } from "./TransportService";
import { PartnerTypeService } from "./PartnerTypeService";
import { VolumeUnitService } from "./VolumeUnitService";
import { WeightUnitService } from "./WeightUnitService";
import { AuditLogActionService } from "./AuditLogActionService";
import { AuditLogEntityService } from "./AuditLogEntityService";
import { AuditLogSourceService } from "./AuditLogSourceService";
import { WorkflowProcessStatusService } from "./WorkflowProcessStatusService";
import { FiltersExpressions } from "../constants/FiltersExpressions";

export class ObjectFieldLookupService {

    private taskTypeService = new TaskTypeService();
    private userService = new UserService();
    private priorityService = new PriorityService();
    private statusService = new StatusService();
    private directionService = new DirectionService();
    private transportService = new TransportService();
    private processInstanceStateService = new ProcessInstanceStateService();
    private workflowService = new WorkflowService();
    private partnerTypeService = new PartnerTypeService();
    private volumeUnitService = new VolumeUnitService();
    private weightUnitService = new WeightUnitService();
    private auditLogActionService = new AuditLogActionService();
    private auditLogEntityService = new AuditLogEntityService();
    private auditLogSourceService = new AuditLogSourceService();
    private workflowProcessStatusService = new WorkflowProcessStatusService();

    getLookupData(objectField: ObjectField): Promise<ServiceResponse<any>> {
        if (!objectField.LookupObjectTable?.IsClosedTable) {
            return this.getEmptyDataPromise();
        }
        switch (objectField.LookupObjectTableCode?.toLowerCase()) {
            case "priority":
                return this.priorityService.getPriorities();
            case "status":
                return this.statusService.getStatuses();
            case "direction":
                return this.directionService.getDirections();
            case "transport":
                return this.transportService.getTransports();
            case "processinstancestate":
                return this.processInstanceStateService.getProcessInstanceStates();
            case "partnertype":
                return this.partnerTypeService.getPartnerTypes();
            case "volumeUnit":
                return this.volumeUnitService.getVolumeUnits();
            case "weightUnit":
                return this.weightUnitService.getWeightUnits();
            case "auditlogaction":
                return this.auditLogActionService.getAuditLogActions();
            case "workflowstatus":
                return this.workflowProcessStatusService.getWorkflowProcessStatuses();
            case "auditlogentity":
                return this.auditLogEntityService.getAuditLogEntities();
            case "auditlogsource":
                return this.auditLogSourceService.getAuditLogSources();
            default:
                return this.getEmptyDataPromise();
        }
    }

    filterLookupData(objectField: ObjectField, searchTerm: string): Promise<ServiceResponse<any>> {
        if (objectField.LookupObjectTable?.IsClosedTable) {
            return this.getEmptyDataPromise();
        }
        switch (objectField.LookupObjectTableCode?.toLowerCase()) {
            case "user": {
                let getByFiltersArguments = CommonFiltersArguments.getAutoCompleteFilters(["FirstName", "LastName"], searchTerm, FilterOperators.Contains);
                return this.userService.getUsersByFilters(getByFiltersArguments);
            }
            case "tasktype": {
                let getByFiltersArguments = CommonFiltersArguments.getAutoCompleteFilters(["Name"], searchTerm, FilterOperators.Contains);
                getByFiltersArguments.Filters.push(CommonFiltersArguments.getInActiveTaskTypeFilter());
                getByFiltersArguments.FiltersExpression = FiltersExpressions.And;
                return this.taskTypeService.getTaskTypesByFilters(getByFiltersArguments);
            }
            case "workflowprocess": {
                let getByFiltersArguments = CommonFiltersArguments.getAutoCompleteFilters(["ProcessName"], searchTerm, FilterOperators.Contains);
                return this.workflowService.getWorkflowsByFilters(getByFiltersArguments);
            }
            case "agent":
            case "contact":
            case "card":
            case "airline":
            case "shipper":
            case "shippingline":
            case "warehouse":
            case "port":
            case "vessel":
            case "country":
            case "packagetype":
            case "currency":
            case "specialservicestype":
            case "entitystatus":
            case "department":
            case "customerteam":
                {
                    let logitudeLookupService = new LogitudeLookupService(objectField.LookupObjectTableCode);
                    let getByFiltersArguments = CommonFiltersArguments.getAutoCompleteFilters(["Name"], searchTerm, FilterOperators.Contains);
                    return logitudeLookupService.getByFilters(getByFiltersArguments);
                }
            default:
                return this.getEmptyDataPromise();
        }
    }

    getSingle(objectField: ObjectField, id: any, isExternalId: boolean = false): Promise<any> {
        if (objectField.LookupObjectTable?.IsClosedTable) {
            return this.getEmptyEntityPromise();
        }
        switch (objectField.LookupObjectTableCode?.toLowerCase()) {
            case "user":
                if (isExternalId) {
                    return this.userService.getByExternalId(id);
                } else {
                    return this.userService.getUser(id);
                }
            case "workflowprocess":
                return this.workflowService.getWorkflow(id);
            case "tasktype":
                return this.taskTypeService.getTaskType(id);
            case "agent":
            case "contact":
            case "card":
            case "airline":
            case "shipper":
            case "shippingline":
            case "warehouse":
            case "port":
            case "vessel":
            case "country":
            case "packagetype":
            case "currency":
            case "specialservicestype":
            case "entitystatus":
            case "department":
            case "customerteam":
                {
                    let logitudeLookupService = new LogitudeLookupService(objectField.LookupObjectTableCode);
                    if (isExternalId) {
                        return logitudeLookupService.getByExternalId(id);
                    } else {
                        return logitudeLookupService.getById(id);
                    }
                }
            default:
                return this.getEmptyEntityPromise();
        }
    }

    private getEmptyDataPromise(): Promise<ServiceResponse<any>> {
        return new Promise((resolve) => {
            resolve({ Data: [], TotalCount: 0, FilteredDataCount: 0, ErrorMessages: [] });
        });
    }

    private getEmptyEntityPromise(): Promise<any> {
        return new Promise((resolve) => {
            resolve(null);
        });
    }

}
